/**
 * Vuex module for the per-kit "pending edits" state machine.
 *
 * Centralizes:
 *   - subsetMode: which subsetting mode the user has currently picked on the
 *     Configure tab radio (full-library | by-style | by-icon). Distinct from
 *     `kit.subset.type` so the radio survives a save where the backend may
 *     normalize an empty subset back to null.
 *   - pendingByStyleIcons: array of {family, style} that the user has toggled
 *     on the Icons tab while in by-style mode (null = no pending toggles).
 *   - pendingFields: any non-subset Configure fields the user has staged
 *     (version, autoAccessibilityEnabled, kitPackageEnabled, …).
 *   - saveState / saveError: shared by the Configure save bar and the Icons
 *     save bar so both surface the same in-flight state.
 */
import { getFamilyStylesByVersion } from 'app/releases'

const initialState = () => ({
  kitToken: null,
  subsetMode: 'full-library',
  savedSubsetMode: 'full-library',
  pendingByStyleIcons: null,
  pendingFields: {},
  saveState: 'idle',
  saveError: null
})

function deriveSubsetMode(subset) {
  if (!subset) return 'full-library'
  if (subset.type === 'custom') return 'by-icon'
  return 'by-style'
}

function styleSetsEqual(a, b) {
  if (a.length !== b.length) return false
  const key = (s) => `${s.family}|${s.style}`
  const aKeys = a.map(key).sort()
  const bKeys = b.map(key).sort()
  return aKeys.every((k, i) => k === bKeys[i])
}

function buildSubsetPayload(state) {
  if (state.subsetMode === 'full-library') return null
  if (state.subsetMode === 'by-style') {
    const icons = state.pendingByStyleIcons || []
    return { type: 'auto', icons: icons.map((s) => ({ family: s.family, style: s.style })) }
  }
  if (state.subsetMode === 'by-icon') {
    return { type: 'custom', icons: [] }
  }
  return null
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    subsetMode: (state) => state.subsetMode,
    savedSubsetMode: (state) => state.savedSubsetMode,
    pendingByStyleIcons: (state) => state.pendingByStyleIcons,
    pendingFields: (state) => state.pendingFields,
    saveState: (state) => state.saveState,
    saveError: (state) => state.saveError,

    isByStyleMode: (state) => state.subsetMode === 'by-style',
    isFullLibraryMode: (state) => state.subsetMode === 'full-library',
    isByIconMode: (state) => state.subsetMode === 'by-icon',

    hasPendingFieldChanges: (state) => Object.keys(state.pendingFields).length > 0,

    hasPendingByStyleChanges: (state, _getters, _rootState, rootGetters) => {
      if (state.pendingByStyleIcons === null) return false
      const kit = rootGetters['kits/currentKit']
      const baseline = kit && kit.subset && Array.isArray(kit.subset.icons) ? kit.subset.icons : []
      return !styleSetsEqual(state.pendingByStyleIcons, baseline)
    },

    subsetModeChanged: (state, _getters, _rootState, rootGetters) => {
      const kit = rootGetters['kits/currentKit']
      if (!kit) return false
      return state.subsetMode !== deriveSubsetMode(kit.subset)
    },

    hasPendingChanges: (_state, getters) => {
      return getters.hasPendingFieldChanges || getters.hasPendingByStyleChanges || getters.subsetModeChanged
    }
  },

  mutations: {
    initForKit(state, { kitToken, subsetMode }) {
      state.kitToken = kitToken
      state.subsetMode = subsetMode
      state.savedSubsetMode = subsetMode
      state.pendingByStyleIcons = null
      state.pendingFields = {}
      state.saveState = 'idle'
      state.saveError = null
    },

    setSubsetMode(state, mode) {
      state.subsetMode = mode
    },

    setSavedSubsetMode(state, mode) {
      state.savedSubsetMode = mode
    },

    setPendingByStyleIcons(state, icons) {
      state.pendingByStyleIcons = icons
    },

    setField(state, { key, value }) {
      state.pendingFields = { ...state.pendingFields, [key]: value }
    },

    removeField(state, key) {
      const next = { ...state.pendingFields }
      delete next[key]
      state.pendingFields = next
    },

    clearPendingByStyleIcons(state) {
      state.pendingByStyleIcons = null
    },

    clearPendingFields(state) {
      state.pendingFields = {}
    },

    setSaveState(state, value) {
      state.saveState = value
    },

    setSaveError(state, value) {
      state.saveError = value
    },

    reset(state) {
      Object.assign(state, initialState())
    }
  },

  actions: {
    initForKit({ commit }, kit) {
      if (!kit) {
        commit('reset')
        return
      }
      commit('initForKit', {
        kitToken: kit.token,
        subsetMode: deriveSubsetMode(kit.subset)
      })
    },

    setSubsetMode({ commit }, mode) {
      commit('setSubsetMode', mode)
      // Switching away from by-style discards any pending icon toggles
      if (mode !== 'by-style') {
        commit('clearPendingByStyleIcons')
      }
    },

    toggleByStyleIcon({ commit, state, rootGetters }, { family, style }) {
      if (state.saveState === 'saving') return
      const kit = rootGetters['kits/currentKit']
      let next = state.pendingByStyleIcons
      if (next === null) {
        const baseline = kit && kit.subset && Array.isArray(kit.subset.icons) ? kit.subset.icons : []
        // Filter the saved baseline against the kit's current version. A kit
        // that was on 6.6.0+ when its By Style subset was saved may include
        // family/styles (e.g. sharp-duotone) that aren't valid at the kit's
        // current version. Carrying them forward causes the backend to reject
        // the save with "invalid family and/or style in kit subset".
        const validStyles = kit ? getFamilyStylesByVersion(kit.version) : []
        const isValid = (s) => validStyles.some((vs) => vs.family === s.family && vs.style === s.style)
        next = baseline.filter(isValid).map((s) => ({ family: s.family, style: s.style }))
      } else {
        next = [...next]
      }
      const idx = next.findIndex((s) => s.family === family && s.style === style)
      if (idx >= 0) {
        next.splice(idx, 1)
      } else {
        next.push({ family, style })
      }
      commit('setPendingByStyleIcons', next)
    },

    setField({ commit, rootGetters }, { key, value }) {
      const kit = rootGetters['kits/currentKit']
      if (kit && Object.prototype.hasOwnProperty.call(kit, key) && kit[key] === value) {
        commit('removeField', key)
        return
      }
      commit('setField', { key, value })
    },

    setFields({ dispatch }, fields) {
      Object.entries(fields).forEach(([key, value]) => {
        dispatch('setField', { key, value })
      })
    },

    cancel({ commit, rootGetters }) {
      const kit = rootGetters['kits/currentKit']
      if (kit) {
        commit('initForKit', {
          kitToken: kit.token,
          subsetMode: deriveSubsetMode(kit.subset)
        })
      } else {
        commit('reset')
      }
    },

    async save({ commit, state, getters, dispatch, rootGetters }) {
      if (state.saveState === 'saving') return
      const kit = rootGetters['kits/currentKit']
      if (!kit) return

      commit('setSaveState', 'saving')
      commit('setSaveError', null)

      const changes = { ...state.pendingFields }

      if (getters.subsetModeChanged || getters.hasPendingByStyleChanges) {
        changes.subset = buildSubsetPayload(state)
      }

      try {
        await dispatch('kits/updateKit', { token: kit.token, ...changes }, { root: true })
        // Keep user's chosen subsetMode (in case backend normalized empty subsets to null)
        // and snapshot it as the now-saved mode so saved-state-only UI (e.g. the
        // "no styles in this Kit" warning) won't fire on local-only mode picks.
        commit('setSavedSubsetMode', state.subsetMode)
        commit('clearPendingFields')
        commit('clearPendingByStyleIcons')
        commit('setSaveState', 'idle')
        commit('setSaveError', null)
        // The backend rebuilds the npm package as a side effect of the kit update.
      } catch (error) {
        commit('setSaveState', 'error')
        commit('setSaveError', (error && error.message) || 'Save failed. Please retry.')
      }
    }
  }
}
